import { inject, Injectable, Signal } from '@angular/core';
import { BusinessConfiguration, HolidayDatesOpeningHours, OpeningPeriods, SpecialDayOpeningHours } from '../state-management/model/interfaces/state.model';
import { WebsiteStateService } from '../state-management/services/website-state.service';
import { MatDialog } from '@angular/material/dialog';
import { SpecialOpeningHoursDialogComponent } from '../components/meta/notification-speical-opening-hours/special-opening-hours-dialog/special-opening-hours-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class SpecialOpeningHoursDialogService {
  readonly dialog = inject(MatDialog);
  private metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  private specialDates: SpecialDayOpeningHours[] = this.metaData().openingHours.specialDayOpeningHours;
  private holidayDates: HolidayDatesOpeningHours[] = this.metaData().openingHours.holidayOpeningHours.days;
  private notificationRange: number = this.metaData().openingHours.differentOpeningHoursNotification.notificationRange;
  private notificationText: string = this.metaData().openingHours.differentOpeningHoursNotification.notificationText;
  private currentDate = new Date();

  constructor(private websiteState: WebsiteStateService) { }

  public notificationDialog(): void {
    const relevantDates = this.getRelevantDates(this.notificationRange);
    if (relevantDates.length > 0) {
      // this.openNotificationDialog(relevantDates);
      this.websiteState.updateChangedOpeningHoursNotification({
        showNotification: true, 
        dates: relevantDates, 
        notificationText: this.metaData().openingHours.differentOpeningHoursNotification.notificationText
      });
    }
  }

  private openNotificationDialog(relevantDates: {date: string; periods: OpeningPeriods[]}[] ): void {
    const dialogRef = this.dialog.open(SpecialOpeningHoursDialogComponent, {
      data: { notificationText: this.notificationText, relevantDates: relevantDates },  
    });
  }

  private getRelevantDates(daysInFuture: number): { date: string; periods: OpeningPeriods[] }[] {
    const relevantDates: { date: string; periods: OpeningPeriods[] }[] = [];
    // Gruppiere zusammenhängende Tage, damit Dialog nicht konsekutive Urlaubstage wegschneidet
    const groupedSpecialDates = this.groupConsecutiveDays(this.specialDates);
    const groupedHolidayDates = this.groupConsecutiveDays(this.holidayDates);
    const addIfRelevant = (dateString: string, periods: OpeningPeriods[]) => {
      const exists = relevantDates.some((entry) => entry.date === dateString);
      if (!exists) {relevantDates.push({ date: dateString, periods: periods })}
    };
    // Prüfe gruppierte SpecialDates
    groupedSpecialDates.forEach((group) => {
      if (group.some((day) => this.isWithinFutureRange(day.date, daysInFuture))) {
        group.forEach((day) => addIfRelevant(day.date, day.periods));
      }
    });
    groupedHolidayDates.forEach((group) => {
      if (group.some((day) => this.isWithinFutureRange(day.date, daysInFuture))) {
        group.forEach((day) => addIfRelevant(day.date, day.periods));
      }
    });
    // Sortieren der relevanten Daten
    relevantDates.sort((a, b) => {
      const dateA = this.parseDate(a.date).getTime();
      const dateB = this.parseDate(b.date).getTime();
      return dateA - dateB;
    });
    return relevantDates;
  }

  private isWithinFutureRange(dateString: string, daysInFuture: number): boolean {
    const [day, month, year] = dateString.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    return (date > this.currentDate && (date.getTime() - this.currentDate.getTime()) / (1000 * 60 * 60 * 24) <= daysInFuture);
  }

  private groupConsecutiveDays(dates: SpecialDayOpeningHours[] | HolidayDatesOpeningHours[]): { date: string; periods: OpeningPeriods[] }[][] {
    // Sortieren
    const sortedDates = dates.sort((a, b) => this.parseDate(a.date).getTime() - this.parseDate(b.date).getTime());
    const groups: { date: string; periods: any[] }[][] = [];
    let currentGroup: { date: string; periods: any[] }[] = [];
    // Gruppieren
    sortedDates.forEach((current, index) => {
      if (currentGroup.length === 0) {
        currentGroup.push(current);
      } else {
        const prevDate = this.parseDate(currentGroup[currentGroup.length - 1].date);
        const currentDate = this.parseDate(current.date);
        const diffInDays = (currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffInDays === 1) {
          currentGroup.push(current);
        } else {
          groups.push(currentGroup);
          currentGroup = [current];
        }
      }
      // Letzte Gruppe hinzufügen
      if (index === sortedDates.length - 1) {
        groups.push(currentGroup);
      }
    });
    return groups;
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('.').map(Number);
    return new Date(year, month - 1, day); // Monate sind 0-basiert in JS
  }
}
